import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, CheckCircle, ArrowRight, TrendingUp, Star, Target, FileText, Shield, Users, Calendar, AlertTriangle, Eye } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/ui/language-selector';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';

// Buyer subscription tiers
const TIERS = {
  essential: {
    priceId:      'price_1TcybjCkUJGKtqQA6SoIwGB9',
    price:        999,
    regularPrice: 1499,
    name: { en: 'Essential', fr: 'Essentiel' },
  },
  essentialPlus: {
    priceId:      'price_1TcycuCkUJGKtqQAX7BA3dSX',
    price:        2199,
    regularPrice: 3299,
    name: { en: 'Essential Plus', fr: 'Essentiel Plus' },
  },
  acquisitionPro: {
    priceId:      'price_1TcyhOCkUJGKtqQAuWnc1P59',
    price:        3999,
    regularPrice: 5999,
    name: { en: 'Acquisition Pro', fr: 'Acquisition Pro' },
  },
  byod: {
    priceId:      'price_1Td0YPCkUJGKtqQAf0h1sMue',
    price:        499,
    regularPrice: 720,
    name: { en: 'BYOD', fr: 'BYOD' },
  },
};

const TRANSLATIONS = {
  en: {
    pageTitle: "Buyer Plans",
    subtitle: "Access the best acquisition opportunities with the plan that fits your needs.",
    
    // Plan titles
    annualSubscription: "Annual subscription",
    payPerDeal: "Pay per deal",
    popular: "Popular",
    
    // Pricing
    regularPrice: "Reg.",
    perYear: "/ year",
    perDeal: "/ deal",
    launchPrice: "Launch price",
    
    // Buttons
    subscribe: "Subscribe",
    submitDeal: "Submit a Deal",
    
    // Section titles
    subscriptionTerms: "Essential & Elite Subscription Terms",
    ndaAccess: "NDA & Financial Access",
    lenderOffers: "Lender Offers",
    calendarManagement: "Calendar Management",
    visitorBuyer: "Visitor Buyer",
    byodOption: "BYOD Option",
    qualifiedProfessionals: "Qualified Professionals",
    platformFees: "Platform Fees",
    
    // Content
    subscriptionTermsText: "Buyers subscribing to the Essential or Elite plan commit to an annual subscription for a fixed period of twelve (12) months. These subscription fees are strictly non-refundable, including in the specific case of a suspension or expulsion pronounced by the platform. It is the Buyer's sole responsibility to maintain a complete, truthful, and meticulously accurate profile.",
    
    ndaAccessText: "Access to a company's complete financial data will only be granted to Essential and Elite Buyers after signing a Non-Disclosure Agreement (NDA). Subsequently, after reviewing the financial documents, the Buyer may send the Seller a contact request. It is expressly understood that AcquizGroup offers no guarantee regarding the selection of the Buyer's profile by Sellers and will not intervene in the Seller's decision to accept or refuse this contact request.",
    
    lenderOffersText: "Please note that financing offers submitted by Lenders will be displayed on the company listing as they are submitted. Each offer will be presented anonymously and in detail, including: the amount granted, the reason for the loan (cash flow, asset, real estate, bridge, etc.), the term (6 months, 1 year, 10 years, etc.), as well as the loan eligibility terms and conditions. The identity of the Lender will only be revealed following the signing of a Letter of Intent (LOI) by all stakeholders.",
    
    calendarText: "The Buyer must ensure their presence at scheduled appointments. The Buyer must also share and keep their calendar up to date on the platform to allow Lenders, Brokers, Sellers, and other platform professionals to schedule appointments according to their availability.",
    
    visitorText: "The Visitor Buyer benefits from limited access to the platform's information and features. They can subscribe to an Essential or Elite plan at any time, thus unlocking full access to information and other features.",
    
    byodText: "The BYOD (Bring Your Own Deal) option is reserved for users who have subscribed to the Essential, Elite, and BYOD plans, allowing them to submit businesses that are not listed on the platform for evaluation and financing consideration. Associated costs vary depending on the chosen plan. However, AcquizGroup cannot guarantee that this business will obtain financing. It is the Buyer's responsibility to obtain and submit all necessary information and documents to generate interest and engagement from Lenders.",
    
    professionalsText: "To facilitate the fluidity of business acquisitions and transfers, AcquizGroup offers a list of qualified professionals, including CPAs, notaries, business lawyers, and tax advisors. However, it is expressly stipulated that the use of their services is in no way mandatory for Users.",
    
    platformFeesText: "Platform management fees equivalent to 1% of the total transaction value will be charged by AcquizGroup.",
    
    financingDisclaimer: "Furthermore, financing approval is solely based on criteria established by Lenders, and AcquizGroup cannot guarantee obtaining such financing. It is therefore the Buyer's responsibility to individually meet Lender requirements or establish necessary partnerships.",
    
    consultantProgram: "Although AcquizGroup has established the CONSULTANT PROGRAM for this purpose, AcquizGroup will not intervene in the Consultants' choice to partner with the Buyer, nor in agreements negotiated and concluded between these private parties.",
    
    ctaTitle: "Ready to start your acquisition journey?",
    ctaSubtitle: "Create your profile and choose the plan that fits your needs",
    registerNow: "Register as Buyer",
    contactTeam: "Contact the Team",
    securePayment: "Secure payment via Stripe.",
  },
  fr: {
    pageTitle: "Forfaits Acheteur",
    subtitle: "Accédez aux meilleures opportunités d'acquisition avec le plan qui correspond à vos besoins.",
    
    // Plan titles
    annualSubscription: "Abonnement annuel",
    payPerDeal: "Paiement par deal",
    popular: "Populaire",
    
    // Pricing
    regularPrice: "Rég.",
    perYear: "/ an",
    perDeal: "/ deal",
    launchPrice: "Prix de lancement",
    
    // Buttons
    subscribe: "Souscrire",
    submitDeal: "Soumettre un Deal",
    
    // Section titles
    subscriptionTerms: "Conditions d'Abonnement Essential & Elite",
    ndaAccess: "Accès NDA & Données Financières",
    lenderOffers: "Offres des Prêteurs",
    calendarManagement: "Gestion du Calendrier",
    visitorBuyer: "Acheteur Visiteur",
    byodOption: "Option BYOD",
    qualifiedProfessionals: "Professionnels Qualifiés",
    platformFees: "Frais de Plateforme",
    
    // Content
    subscriptionTermsText: "Les Acheteurs souscrivant au forfait Essential ou Elite s'engagent à un abonnement annuel d'une durée ferme de douze (12) mois. Ces frais d'abonnement sont strictement non remboursables, y compris dans le cas spécifique d'une suspension ou d'une expulsion prononcée par la plateforme. Il incombe à l'Acheteur l'entière responsabilité de maintenir un profil complet, véridique et méticuleusement exact.",
    
    ndaAccessText: "L'accès aux données financières complètes d'une entreprise ne sera accordé aux Acheteurs Essential et Elite qu'après la signature d'un Accord de Non-Divulgation (NDA). Par la suite, après avoir pris connaissance des documents financiers, l'Acheteur pourra faire parvenir au Vendeur une demande de prise de contact. Il est expressément entendu qu'AcquizGroup n'offre aucune garantie quant à la sélection du profil de l'Acheteur par les Vendeurs et n'interviendra pas dans la décision du Vendeur d'accepter ou non cette prise de contact.",
    
    lenderOffersText: "Veuillez noter que les offres de financement soumises par les Prêteurs seront affichées sur l'annonce de l'entreprise au fur et à mesure de leur soumission. Chaque offre sera présentée de manière anonyme et détaillée, incluant : le montant octroyé, la raison du prêt (flux de trésorerie, actif, immobilier, prêt-relais (bridge), etc.), le terme (6 mois, 1 an, 10 ans, etc.), ainsi que les termes et conditions d'éligibilité au prêt. L'identité du Prêteur ne sera révélée qu'à la suite de la signature d'une Lettre d'Intention (LOI) par toutes les parties prenantes.",
    
    calendarText: "L'Acheteur est tenu de s'assurer de sa présence aux rendez-vous cédulés. L'Acheteur doit également partager et maintenir à jour son calendrier sur la plateforme afin de permettre aux Prêteurs, Courtiers, Vendeurs et autres professionnels de la plateforme de planifier des rendez-vous en fonction de ses disponibilités.",
    
    visitorText: "L'Acheteur Visiteur bénéficie d'un accès limité aux informations et fonctionnalités de la plateforme. Il peut souscrire à tout moment à un forfait Essential ou Elite, débloquant ainsi l'accès complet aux informations et autres fonctionnalités.",
    
    byodText: "L'option BYOD (Bring Your Own Deal) est réservée aux usagers ayant souscrit au forfait Essential, Elite et BYOD, leur permettant de soumettre des entreprises qui ne sont pas listées sur la plateforme pour évaluation et considération de financement. Les coûts associés varient en fonction du forfait choisi. Toutefois, AcquizGroup ne peut garantir que cette entreprise obtiendra un financement. Il incombe à l'Acheteur d'obtenir et de soumettre toutes les informations et documents nécessaires pour susciter l'intérêt et l'engagement des Prêteurs.",
    
    professionalsText: "Afin de faciliter la fluidité des acquisitions et des transferts d'entreprises, AcquizGroup propose une liste de professionnels qualifiés, incluant des CPA, notaires, avocats en droit des affaires et conseillers fiscaux (Tax Advisors). Il est toutefois expressément stipulé que l'utilisation de leurs services n'est en aucun cas obligatoire pour les Utilisateurs.",
    
    platformFeesText: "Des frais de gestion de plate-forme équivalents à 1% de la valeur totale de la transaction seront facturés par AcquizGroup.",
    
    financingDisclaimer: "De plus, l'approbation du financement repose uniquement sur les critères établis par les Prêteurs, et AcquizGroup ne peut garantir l'obtention de ce financement. Il est donc de la responsabilité de l'Acheteur de satisfaire individuellement aux exigences des Prêteurs ou de constituer les partenariats nécessaires.",
    
    consultantProgram: "Bien qu'AcquizGroup ait institué le PROGRAMME CONSULTANT à cet effet, AcquizGroup n'interviendra ni dans le choix des Consultants de s'associer avec l'Acheteur, ni dans les accords négociés et conclus entre ces parties privées.",
    
    ctaTitle: "Prêt à commencer votre parcours d'acquisition?",
    ctaSubtitle: "Créez votre profil et choisissez le plan qui correspond à vos besoins",
    registerNow: "S'inscrire comme Acheteur",
    contactTeam: "Contacter l'équipe",
    securePayment: "Paiement sécurisé via Stripe.",
  }
};

const BuyerPayment: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  const essentialFeatures = language === 'fr' ? [
    "Accès complet au Tableau de bord Acheteur",
    "Accès complet au Deal Monitor",
    "Recherche avancée d'entreprises",
    "Signature de NDA électronique",
    "Accès aux offres des prêteurs (anonyme avant LOI)",
    "Contact direct avec les vendeurs",
    "Messagerie sécurisée",
    "Documents confidentiels inclus",
    "BYOD à 300$ par deal soumis",
  ] : [
    "Full access to Buyer Dashboard",
    "Full access to Deal Monitor",
    "Advanced business search",
    "Electronic NDA signing",
    "Access to lender offers (anonymous until LOI)",
    "Direct contact with sellers",
    "Secure messaging",
    "Confidential documents included",
    "BYOD at $300 per deal submitted",
  ];

  const essentialPlusFeatures = language === 'fr' ? [
    "Tous les avantages Essential",
    "BYOD à 300$ par deal soumis",
    "Rapport d'analyse de marché INCLUS",
    "Support prioritaire dédié",
    "Accès anticipé aux nouvelles annonces",
    "Badge Acheteur Élite sur profil",
    "Boîte de réception sécurisée dédiée",
    "Stockage sécurisé des documents (Data Room)",
    "Outil de vidéoconférence",
    "Accès à la plateforme E-Learning",
  ] : [
    "All Essential benefits",
    "BYOD at $300 per deal submitted",
    "Market Analysis Report INCLUDED",
    "Dedicated priority support",
    "Early access to new listings",
    "Elite Buyer badge on profile",
    "Secure dedicated deal MailBox",
    "Secure document storage (Data Room)",
    "Video conferencing tool",
    "Access to E-Learning platform",
  ];

  const byodFeatures = language === 'fr' ? [
    "Accès complet à l'Espace Acheteur",
    "Soumission pour analyse et préfinancement",
    "Offres des prêteurs sur votre tableau de bord",
    "MailBox sécurisée dédiée au deal",
    "Dépôt de document sécurisé (Data Room)",
    "Outil de visioconférence",
    "Accès à la plateforme E-Learning",
  ] : [
    "Full access to Buyer Dashboard",
    "Submission for analysis and pre-financing",
    "Lender offers on your dashboard",
    "Secure dedicated deal MailBox",
    "Secure document storage (Data Room)",
    "Video conferencing tool",
    "Access to E-Learning platform",
  ];

  const visitorFeatures = language === 'fr' ? [
    "Parcourir les annonces publiques",
    "Voir les résumés d'entreprises",
    "Accès aux répertoires consultants & experts",
    "Créer une liste de favoris",
    "Accès à la plateforme E-Learning",
  ] : [
    "Browse public listings",
    "View business summaries",
    "Access consultant & expert directories",
    "Create a favorites list",
    "Access to E-Learning platform",
  ];

  const essentialSavings      = Math.round(((1499 - 999)  / 1499) * 100);  // 33%
  const essentialPlusSavings  = Math.round(((3299 - 2199) / 3299) * 100);  // 33%
  const acquisitionProSavings = Math.round(((5999 - 3999) / 5999) * 100);  // 33%
  const byodSavings           = Math.round(((720  - 499)  / 720)  * 100);  // 31%

  const acquisitionProFeatures = language === 'fr' ? [
    "Tous les avantages Essential Plus",
    "Rapports Deal Quality illimités",
    "Rapports Financing Feasibility illimités",
    "3 Market Studies inclus/an",
    "2 Plans Stratégiques inclus/an",
    "2 Market Comparison inclus/an",
    "Badge Acheteur Sérieux Vérifié sur le profil",
    "Priorité maximale sur les demandes de contact",
    "BYOD : 199$/deal soumis",
    "Accès E-Learning complet",
    "Frais de gestion réduits : 0,75% au lieu de 1%",
  ] : [
    "All Essential Plus benefits",
    "Unlimited Deal Quality Reports",
    "Unlimited Financing Feasibility Reports",
    "3 Market Studies included/year",
    "2 Strategic Plans included/year",
    "2 Market Comparison Reports included/year",
    "Badge Verified Serious Buyer on profile",
    "Maximum priority on contact requests",
    "BYOD: $199/deal submitted",
    "Full E-Learning access",
    "Reduced management fee: 0.75% instead of 1%",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 font-sans">
      <SiteHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          {/* Language Toggle */}
          <div className="flex justify-center mb-6">
            <LanguageSelector />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.pageTitle}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            
            {/* Visitor Plan */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-slate-500 to-slate-600 p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {language === 'fr' ? 'Visiteur' : 'Visitor'}
                </h2>
                <p className="text-white/70 text-sm">
                  {language === 'fr' ? 'Accès de base' : 'Basic access'}
                </p>
              </div>
              
              {/* Pricing */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold text-slate-600">
                    {language === 'fr' ? 'Gratuit' : 'Free'}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-slate-500 font-semibold">
                    {language === 'fr' ? 'Aucun engagement' : 'No commitment'}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 flex-grow">
                <div className="space-y-3">
                  {visitorFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                      <span className="ml-2 text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => navigate('/register/buyers')}
                  className="w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 bg-slate-600 text-white hover:bg-slate-700"
                >
                  {language === 'fr' ? "S'inscrire gratuitement" : 'Sign up free'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Essential Plan */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {TIERS.essential.name[language]}
                </h2>
                <p className="text-white/70 text-sm">{t.annualSubscription}</p>
              </div>
              
              {/* Pricing */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-slate-500 line-through">
                    ${TIERS.essential.regularPrice.toFixed(2)}
                  </span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    -{essentialSavings}%
                  </span>
                </div>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold text-[#1D314D]">
                    ${TIERS.essential.price.toFixed(2)}
                  </span>
                  <span className="ml-2 text-lg font-medium text-slate-500">
                    {t.perYear}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-[#CEAB65] font-semibold">
                    🚀 {t.launchPrice}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 flex-grow">
                <div className="space-y-3">
                  {essentialFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                      <span className="ml-2 text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button - Fixed at bottom */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => navigate('/register/buyers')}
                  className="w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90"
                >
                  {t.subscribe}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Essential Plus Plan */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col relative ring-2 ring-[#CEAB65]">
              <div className="absolute top-0 right-0 bg-[#CEAB65] text-[#1D314D] px-4 py-1 text-sm font-bold rounded-bl-lg z-10">
                {t.popular}
              </div>

              {/* Header */}
              <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] p-6 text-center">
                <div className="w-12 h-12 bg-[#CEAB65]/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-[#CEAB65]" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {TIERS.essentialPlus.name[language]}
                </h2>
                <p className="text-white/70 text-sm">{t.annualSubscription}</p>
              </div>
              
              {/* Pricing */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-slate-500 line-through">
                    ${TIERS.essentialPlus.regularPrice.toFixed(2)}
                  </span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    -{essentialPlusSavings}%
                  </span>
                </div>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold text-[#CEAB65]">
                    ${TIERS.essentialPlus.price.toFixed(2)}
                  </span>
                  <span className="ml-2 text-lg font-medium text-slate-500">
                    {t.perYear}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-[#CEAB65] font-semibold">
                    🚀 {t.launchPrice}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 flex-grow">
                <div className="space-y-3">
                  {essentialPlusFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                      <span className="ml-2 text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button - Fixed at bottom */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => navigate('/register/buyers')}
                  className="w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90"
                >
                  {t.subscribe}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Acquisition Pro Plan */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col relative ring-2 ring-[#CEAB65]">
              <div className="absolute top-0 right-0 bg-[#CEAB65] text-[#1D314D] px-4 py-1 text-sm font-bold rounded-bl-lg z-10">
                {language === 'fr' ? 'Meilleure Valeur' : 'Best Value'}
              </div>

              {/* Header */}
              <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] p-6 text-center">
                <div className="w-12 h-12 bg-[#CEAB65]/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-[#CEAB65]" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {TIERS.acquisitionPro.name[language]}
                </h2>
                <p className="text-white/70 text-sm">{t.annualSubscription}</p>
              </div>

              {/* Pricing */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-slate-500 line-through">
                    ${TIERS.acquisitionPro.regularPrice.toLocaleString()}
                  </span>
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    -{acquisitionProSavings}%
                  </span>
                </div>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold text-[#CEAB65]">
                    ${TIERS.acquisitionPro.price.toLocaleString()}
                  </span>
                  <span className="ml-2 text-lg font-medium text-slate-500">
                    {t.perYear}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-[#CEAB65] font-semibold">
                    🚀 {t.launchPrice}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 flex-grow">
                <div className="space-y-3">
                  {acquisitionProFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                      <span className="ml-2 text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => navigate('/register/buyers')}
                  className="w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90"
                >
                  {t.subscribe}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>

            {/* BYOD Plan */}
            <div className="bg-white shadow-2xl rounded-xl overflow-hidden flex flex-col relative">
              <div className="absolute top-0 right-0 bg-orange-500 text-white px-4 py-1 text-sm font-bold rounded-bl-lg z-10">
                BYOD
              </div>

              {/* Header */}
              <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] p-6 text-center">
                <div className="w-12 h-12 bg-orange-500/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Bring Your Own Deal
                </h2>
                <p className="text-white/70 text-sm">{t.payPerDeal}</p>
              </div>
              
              {/* Pricing */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-slate-500 line-through">
                    ${TIERS.byod.regularPrice}
                  </span>
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    -{byodSavings}%
                  </span>
                </div>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-extrabold text-orange-500">
                    ${TIERS.byod.price}
                  </span>
                  <span className="ml-2 text-lg font-medium text-slate-500">
                    {t.perDeal}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <span className="text-sm text-orange-500 font-semibold">
                    🚀 {t.launchPrice}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div className="p-6 flex-grow">
                <div className="space-y-3">
                  {byodFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span className="ml-2 text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button - Fixed at bottom */}
              <div className="p-6 pt-0 mt-auto">
                <button
                  onClick={() => navigate('/register/buyers')}
                  className="w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 bg-orange-500 text-white hover:bg-orange-600"
                >
                  {t.submitDeal}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Information Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto space-y-12">
          
          {/* Subscription Terms */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#1D314D]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#1D314D]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.subscriptionTerms}</h3>
                <p className="text-slate-700 leading-relaxed">{t.subscriptionTermsText}</p>
              </div>
            </div>
          </div>

          {/* NDA Access */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#CEAB65]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-[#CEAB65]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.ndaAccess}</h3>
                <p className="text-slate-700 leading-relaxed mb-4">{t.ndaAccessText}</p>
                <p className="text-slate-600 leading-relaxed italic">{t.financingDisclaimer}</p>
              </div>
            </div>
          </div>

          {/* Lender Offers */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.lenderOffers}</h3>
                <p className="text-slate-700 leading-relaxed">{t.lenderOffersText}</p>
              </div>
            </div>
          </div>

          {/* Calendar Management */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.calendarManagement}</h3>
                <p className="text-slate-700 leading-relaxed">{t.calendarText}</p>
              </div>
            </div>
          </div>

          {/* Visitor Buyer */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.visitorBuyer}</h3>
                <p className="text-slate-700 leading-relaxed">{t.visitorText}</p>
              </div>
            </div>
          </div>

          {/* BYOD Option */}
          <div className="bg-orange-50 rounded-xl p-8 shadow-sm border border-orange-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.byodOption}</h3>
                <p className="text-slate-700 leading-relaxed">{t.byodText}</p>
              </div>
            </div>
          </div>

          {/* Consultant Program Note */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <p className="text-amber-800">{t.consultantProgram}</p>
          </div>

          {/* Qualified Professionals */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#1D314D]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-[#1D314D]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">{t.qualifiedProfessionals}</h3>
                <p className="text-slate-700 leading-relaxed">{t.professionalsText}</p>
              </div>
            </div>
          </div>

          {/* Platform Fees */}
          <div className="bg-[#1D314D] rounded-xl p-8 text-center">
            <h3 className="text-xl font-bold text-white mb-3">{t.platformFees}</h3>
            <p className="text-white/80 text-lg">{t.platformFeesText}</p>
          </div>

          {/* Management Fees */}
          <div className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#CEAB65]/20 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#CEAB65]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1D314D] mb-3">
                  {language === 'fr' ? 'Frais de gestion' : 'Management Fees'}
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  {language === 'fr'
                    ? "AcquizGroup perçoit des frais de gestion de plateforme équivalents à 1% de la valeur totale de la transaction lors de la conclusion réussie d'une acquisition. Les abonnés Acquisition Pro bénéficient d'un taux réduit de 0,75%."
                    : "AcquizGroup charges a platform management fee of 1% of the total transaction value upon successful closing of an acquisition. Acquisition Pro subscribers benefit from a reduced rate of 0.75%."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1D314D] to-[#2E4663]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-xl text-white/80 mb-8">
            {t.ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register/buyers')}
              className="bg-[#CEAB65] text-[#1D314D] px-8 py-4 rounded-lg font-semibold hover:bg-[#CEAB65]/90 transition-colors"
            >
              {t.registerNow}
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              {t.contactTeam}
            </button>
          </div>
          <div className="text-center mt-8 text-white/60 text-sm">
            <CreditCard className="inline w-4 h-4 mr-1 mb-0.5" /> 
            {t.securePayment}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BuyerPayment;
